/**
 * 클라이언트 엔트리포인트.
 *
 * 환경별 분기:
 *  - SSR 모드 (npm run dev:ssr / 프로덕션): root에 SSR HTML이 채워져 있으므로 hydrateRoot
 *  - Vite dev 모드 (npm run dev): root가 비어있으므로 createRoot
 *
 * 두 케이스를 모두 지원하지 않으면 vite dev에서 React #418 hydration mismatch 발생.
 * 영구지침: docs/develop/seo/ssr-spec.md §2.3
 */
import { StrictMode, type ReactNode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider, HydrationBoundary } from '@tanstack/react-query'
import SentryErrorBoundary from '@/components/common/SentryErrorBoundary'
import ToastContainer from '@/components/ui/Toast'
import AppRoutes from '@/app/AppRoutes'
import { HostContext } from '@/hooks/useHost'
import { initSentry } from '@/lib/sentry'
import { initGlobalErrorHandlers } from '@/lib/errorReporter'
import { clearChunkReloadFlag } from '@/lib/lazyWithRetry'
import { useAuthStore } from '@/stores/authStore'
import { registerServiceWorker } from '@/lib/pwa/serviceWorkerRegistration'
import { initInstallPromptCapture } from '@/lib/pwa/installPrompt'
import '@/styles/global.css'

// 브라우저 전용 초기화
initSentry()
initGlobalErrorHandlers()
// 정상 hydrate 시점에 chunk reload 플래그 초기화
// (이전 배포 후 한 번 새로고침으로 복구된 사용자가, 추후 또 다른 chunk 실패 시
//  다시 reload를 시도할 수 있도록 한다.)
clearChunkReloadFlag()
// PWA: 설치 프롬프트 캡처 + 방문 카운트 시작
// 영구지침: docs/develop/pwa/pwa-spec.md §3.3, §3.5
initInstallPromptCapture()

// 서버에서 전달한 TanStack Query dehydrated state
const dehydratedState = (window as any).__REACT_QUERY_STATE__

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: { retry: 0 },
  },
})

const rootEl = document.getElementById('root')!

const clientHost = typeof window !== 'undefined' ? window.location.host : null

const tree: ReactNode = (
  <StrictMode>
    <SentryErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <HostContext.Provider value={clientHost}>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </HostContext.Provider>
          </HydrationBoundary>
          <ToastContainer />
        </QueryClientProvider>
      </HelmetProvider>
    </SentryErrorBoundary>
  </StrictMode>
)

// SSR 여부 감지: root에 자식 노드가 있으면 SSR이 미리 채운 것 → hydrate.
// 빈 root이면 vite dev (CSR) → createRoot.
const isHydrating = rootEl.hasChildNodes()

if (isHydrating) {
  hydrateRoot(rootEl, tree, {
    // 하이드레이션 mismatch 등 복구 가능한 오류를 콘솔/Sentry로 가시화한다.
    // production minified React 환경에서는 #418/#423 메시지가 모호하므로
    // 여기서 추가 컨텍스트를 남겨 디버깅을 돕는다.
    onRecoverableError: (error, errorInfo) => {
      try {
        // eslint-disable-next-line no-console
        console.error('[hydrateRoot] recoverable error', error, errorInfo)
      } catch { /* ignore */ }
    },
  })
} else {
  createRoot(rootEl).render(tree)
}

// React 하이드레이션 완료 후 Zustand persist를 명시적으로 rehydrate.
// hydrateRoot는 비동기적으로 첫 렌더를 수행하므로, setTimeout으로 다음 tick에
// rehydrate를 호출해야 React의 첫 렌더가 SSR과 일치한 상태(isAuthenticated=false)로
// 완료된 후 store가 갱신됨. 그 다음 정상적인 React 리렌더링으로 인증 상태 반영.
setTimeout(() => {
  useAuthStore.persist.rehydrate()
}, 0)

// PWA Service Worker 등록 (프로덕션 빌드에서만 동작)
// 영구지침: docs/develop/pwa/pwa-spec.md §3.3
void registerServiceWorker()
